import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirestoreDb } from '@/lib/firebase';
import { useOrg } from '@/contexts/OrgContext';
import { textLinkDangerVariants } from '@/components/ui/button-variants';
import { Button } from '@/components/ui/Button';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { cn } from '@/lib/cn';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';

type TaxDef = {
  id: string;
  name: string;
  nameAr?: string;
  ratePercent?: number;
  isActive: boolean;
};

export function TaxesPage() {
  const { t, i18n } = useTranslation();
  const { orgId } = useOrg();
  const online = useOnlineStatus();
  const [name, setName] = useState('');
  const [rate, setRate] = useState('14');

  const mapDoc = useCallback(
    (id: string, data: Record<string, unknown>) => ({ id, ...data }) as TaxDef,
    []
  );

  const {
    rows: taxes,
    loading,
    pageIndex,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    pageSize,
    setPageSize,
    refresh
  } = useFirestorePageQuery<TaxDef>({
    orgId,
    collectionPath: 'taxDefinitions',
    orderByField: 'name',
    orderDirection: 'asc',
    mapDoc
  });

  async function addTax() {
    if (!orgId || !online || !name.trim()) return;
    await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'taxDefinitions'), {
      name: name.trim(),
      nameAr: name.trim(),
      ratePercent: Number(rate) || 0,
      isActive: true,
      isInclusive: false,
      createdAt: serverTimestamp()
    });
    setName('');
    refresh();
  }

  async function toggleActive(tax: TaxDef) {
    if (!orgId || !online) return;
    await updateDoc(doc(getFirestoreDb(), 'organizations', orgId, 'taxDefinitions', tax.id), {
      isActive: !tax.isActive,
      updatedAt: serverTimestamp()
    });
    refresh();
  }

  async function removeTax(id: string) {
    if (!orgId || !online) return;
    await deleteDoc(doc(getFirestoreDb(), 'organizations', orgId, 'taxDefinitions', id));
    refresh();
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.taxes')}</h1>
      <div className="mt-6 flex flex-wrap gap-2">
        <input
          className="rounded border px-3 py-2"
          placeholder={t('taxes.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!online}
        />
        <MoneyInput
          className="w-24"
          placeholder={t('taxes.ratePlaceholder')}
          value={rate}
          onValueChange={setRate}
          disabled={!online}
          aria-label={t('taxes.ratePlaceholder')}
        />
        <Button disabled={!online} onClick={() => void addTax()}>
          {t('taxes.add')}
        </Button>
      </div>
      <PaginatedTable
        className="mt-6"
        loading={loading}
        pageIndex={pageIndex}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={nextPage}
        onPrev={prevPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
      >
        <ul className="divide-y divide-[var(--fv-border)]">
          {taxes.map((tax) => (
            <li
              key={tax.id}
              className="flex items-center justify-between px-4 py-3 text-sm"
            >
              <span>
                {i18n.language === 'ar' ? tax.nameAr ?? tax.name : tax.name} — {tax.ratePercent}%
                {!tax.isActive && (
                  <span className="ms-2 text-[var(--fv-text-muted)]">({t('taxes.inactive')})</span>
                )}
              </span>
              <span className="flex gap-2">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  disabled={!online}
                  onClick={() => void toggleActive(tax)}
                >
                  {tax.isActive ? t('taxes.deactivate') : t('taxes.activate')}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className={cn(textLinkDangerVariants(), 'h-auto min-h-0')}
                  disabled={!online}
                  onClick={() => void removeTax(tax.id)}
                >
                  {t('common.delete')}
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </PaginatedTable>
    </div>
  );
}
